import { useId, useState, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { RefreshIcon } from '../../components/icons';
import type { FreeRegens, NewVersionBody, QrVersion } from './types';

export interface NewVersionFormProps {
  /** Versiones de QR Art vigentes: de ahí sale el prompt a reutilizar (R1.3). */
  sources: QrVersion[];
  freeRegens?: FreeRegens | null;
  /** Versión elegida con «Nueva con este prompt». */
  sourceId?: string | null;
  onSourceChange: (id: string) => void;
  generating?: boolean;
  error?: string | null;
  onSubmit: (body: NewVersionBody) => void;
}

/** Cuántas regeneraciones gratis quedan, o `0` si ya no hay. */
function freeLeft(freeRegens?: FreeRegens | null): number {
  if (!freeRegens) return 0;
  return Math.max(0, freeRegens.limit - freeRegens.used);
}

/** Tarjeta de pedido de una versión nueva (R1.3, R2): el botón dice el costo antes de generar. */
export function NewVersionForm({ sources, freeRegens, sourceId, onSourceChange, generating = false, error, onSubmit }: NewVersionFormProps) {
  const id = useId();
  const [prompt, setPrompt] = useState('');
  const [label, setLabel] = useState('');
  const newPrompt = sourceId === 'new-prompt';
  const left = freeLeft(freeRegens);
  const costText = left > 0 ? `gratis (${left === 1 ? 'queda 1' : `quedan ${left}`})` : 'cuenta 1 QR Art de tu plan';

  function submit(e: FormEvent) {
    e.preventDefault();
    if (generating) return;
    if (newPrompt) {
      if (!prompt.trim()) return;
      onSubmit({ source: 'new-prompt', prompt: prompt.trim(), label: label.trim() || undefined });
      return;
    }
    onSubmit({ source: 'same-prompt', label: label.trim() || undefined });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 rounded-lg border-2 border-dashed border-line bg-white p-3" aria-label="Nueva versión de QR Art">
      <h3 className="font-display text-base font-bold text-ink">Nueva versión de QR Art</h3>

      <label htmlFor={`${id}-src`} className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
        Partir de
        <select
          id={`${id}-src`}
          value={sourceId ?? 'new-prompt'}
          onChange={(e) => onSourceChange(e.target.value)}
          className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
        >
          {sources.map((v) => (
            <option key={v.id} value={v.id}>
              El prompt de «{v.label ?? 'QR Art sin nombre'}», con otra semilla
            </option>
          ))}
          <option value="new-prompt">Un prompt nuevo</option>
        </select>
      </label>

      {newPrompt && (
        <label htmlFor={`${id}-prompt`} className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Prompt nuevo
          <input
            id={`${id}-prompt`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ej.: cordillera nevada al atardecer"
            className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
          />
        </label>
      )}

      <label htmlFor={`${id}-label`} className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
        Nombre de la versión (opcional)
        <input
          id={`${id}-label`}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          placeholder="Ej.: Volantines"
          className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
        />
      </label>

      <Button type="submit" size="sm" loading={generating} disabled={newPrompt && !prompt.trim()}>
        <RefreshIcon />
        Generar · {costText}
      </Button>
      {error && (
        <p role="alert" className="text-xs font-medium text-error-text">
          {error}
        </p>
      )}
    </form>
  );
}
